import React, { useEffect, useRef, useState } from 'react';
import { feature } from 'topojson-client';
import dmaData from './DMA_shp.json';
import usData from './us.json'; // Load the US map TopoJSON

// ux
import LoadingAnimationSimple from '../ux/LoadingAnimationSimple';
import RadioButton from '../ux/RadioButton';

const GeoData = ({
  loadingGeoData,
  brandName,
  celebName,
  brandGeoData = [],
  celebrityGeoData = [],
  handleRetry
}) => {
  const [selectedData, setSelectedData] = useState('brand');
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const nameToCoordinatesMap = useRef({});

  // Create maps to clean and transform region names
  useEffect(() => {
    if (dmaData && dmaData.features) {
      dmaData.features.forEach((feature) => {
        const cleanedDmaName = feature.properties.dma_name
          .replace(/,/g, '')
          .replace(/[()]/g, '')
          .replace(/-/g, '')
          .replace(/\./g, '')
          .replace(/&/g, '')
          .replace(/\s/g, '')
          .toLowerCase();
        nameToCoordinatesMap.current[cleanedDmaName] = {
          latitude: feature.properties.latitude,
          longitude: feature.properties.longitude,
          name: feature.properties.dma_name
        };
      });
    } else {
      console.warn('dmaData.features is not available.');
    }
  }, []);

  // region offset values
  const regionOffsets = {
    honoluluhi: { x: -3, y: -3 },
    anchorageak: { x: -65, y: 0 },
    fairbanksak: { x: 0, y: 0 },
    juneauak: { x: -3, y: -2 }
  };

  // Transform the data to include coordinates and region names
  const transformData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data
      .map((item) => {
        // Clean the region name
        let cleanedRegionName = item.region
          .replace(/,/g, '')
          .replace(/[()]/g, '')
          .replace(/-/g, '')
          .replace(/\./g, '')
          .replace(/&/g, '')
          .replace(/\s/g, '')
          .toLowerCase();

        // Get coordinates from nameToCoordinatesMap
        let coordinates = nameToCoordinatesMap.current[cleanedRegionName];

        // Parse the geo_score and handle NaN values
        let score = parseFloat(item.geo_score);
        if (isNaN(score)) {
          score = 0;
        }

        return {
          ...item,
          ...coordinates,
          geo_score: score,
          cleanedRegionName // Add cleaned region name for reference
        };
      })
      .filter((item) => item.latitude && item.longitude); // Ensure valid lat/lon
  };

  useEffect(() => {
    const dataToUse =
      selectedData === 'brand' ? brandGeoData : celebrityGeoData;

    if (!Array.isArray(dataToUse) || dataToUse.length === 0) {
      // Clear the SVG if no data is available
      d3.select(svgRef.current).selectAll('*').remove();
      return;
    }

    const drawHexbinMap = (data) => {
      const transformedData = transformData(data);

      if (transformedData.length === 0) {
        // Clear the SVG if transformed data is empty
        d3.select(svgRef.current).selectAll('*').remove();
        return;
      }

      const svg = d3.select(svgRef.current);
      const width = 488;
      const height = (600 * width) / 928; // Extend the height slightly

      const projection = d3
        .geoAlbersUsa()
        .scale((4 / 3) * width)
        .translate([width / 2, height / 2]);

      const geoPath = d3.geoPath().projection(projection);

      // Define hexbin
      const hexbin = d3
        .hexbin()
        .radius(10) // Reduced radius to decrease overlap
        .extent([
          [0, 0],
          [width, height]
        ])
        .x((d) => d.xy[0])
        .y((d) => d.xy[1]);

      // Convert data to coordinates
      const coordinates = transformedData
        .map((d) => {
          let xy = projection([d.longitude, d.latitude]);

          // Check if the region needs to be repositioned
          const offset = regionOffsets[d.cleanedRegionName];
          if (offset) {
            xy = [xy[0] + offset.x, xy[1] + offset.y];
          }

          if (!xy) return null; // Skip if projection fails

          return {
            xy,
            score: d.geo_score,
            name: d.name
          };
        })
        .filter((d) => d !== null); // Remove null entries

      let bins = hexbin(coordinates).map((d) => {
        if (d.length === 1) {
          d.score = d[0].score;
        } else {
          d.score = d3.mean(d, (p) => p.score);
        }
        d.name = d[0].name;
        return d;
      });

      // Sort bins by score in ascending order (rendering highest score last)
      bins.sort((a, b) => a.score - b.score);

      // Create color and radius scales based on fixed domain
      const color = d3
        .scaleSequential(
          d3.interpolateRgbBasis([
            '#03ffff', // light blue
            '#0055fe' // dark blue
          ])
        )
        .domain([0, 100]);

      const radius = d3
        .scaleSqrt()
        .domain([0, 100])
        .range([1, hexbin.radius() * Math.SQRT2]);

      // Clear any existing content
      svg.selectAll('*').remove();

      // Append the state mesh first
      svg
        .append('path')
        .datum(feature(usData, usData.objects.states))
        .attr('fill', 'none')
        .attr('stroke', '#777')
        .attr('stroke-width', 0.5)
        .attr('stroke-linejoin', 'round')
        .attr('d', geoPath);

      // Append the hexagons
      const hexagons = svg
        .append('g')
        .selectAll('path')
        .data(bins)
        .join('path')
        .attr('transform', (d) => `translate(${d.x},${d.y})`)
        .attr('d', (d) => hexbin.hexagon(radius(d.score)))
        .attr('fill', (d) => color(d.score))
        .attr('stroke', (d) => d3.lab(color(d.score)).darker())
        .attr('stroke-width', 0.5);

      // Implement mouseover effect for tooltips
      svg
        .on('mousemove', function (event) {
          const [mouseX, mouseY] = d3.pointer(event);
          let closestHex;
          let minDistance = Infinity;
          bins.forEach((d) => {
            const distance = Math.sqrt(
              Math.pow(mouseX - d.x, 2) + Math.pow(mouseY - d.y, 2)
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestHex = d;
            }
          });
          if (closestHex) {
            hexagons.attr('stroke', (d) =>
              d === closestHex ? 'white' : d3.lab(color(d.score)).darker()
            );
            hexagons.attr('stroke-width', (d) => (d === closestHex ? 2 : 0.5));
            const tooltip = d3.select(tooltipRef.current);
            const svgRect = svgRef.current.getBoundingClientRect();
            const [x, y] = [
              event.clientX - svgRect.left,
              event.clientY - svgRect.top
            ];
            tooltip
              .style('opacity', 1)
              .html(`Region: ${closestHex.name}<br>Score: ${closestHex.score}`)
              .style('left', `${x + 5}px`)
              .style('top', `${y - 28}px`);
          }
        })
        .on('mouseout', () => {
          hexagons.attr('stroke', (d) => d3.lab(color(d.score)).darker());
          hexagons.attr('stroke-width', 0.5);
          d3.select(tooltipRef.current).style('opacity', 0);
        });
    };

    drawHexbinMap(dataToUse);
  }, [loadingGeoData, brandGeoData, celebrityGeoData, selectedData]);

  return (
    <>
      {loadingGeoData ? (
        <div className="loading-animation-flex">
          <LoadingAnimationSimple />
        </div>
      ) : brandGeoData != null && celebrityGeoData != null ? (
        <div className="fuselight-geo-data">
          <div className="flex-container">
            <div className="data-selector">
              <RadioButton
                className={
                  selectedData === 'brand'
                    ? 'fuselight-radio active'
                    : 'fuselight-radio'
                }
                label={brandName}
                name="dataSeries"
                value="brand"
                checked={selectedData === 'brand'}
                onChange={() => setSelectedData('brand')}
              />
              <RadioButton
                className={
                  selectedData === 'celebrity'
                    ? 'fuselight-radio active'
                    : 'fuselight-radio'
                }
                label={celebName}
                name="dataSeries"
                value="celebrity"
                checked={selectedData === 'celebrity'}
                onChange={() => setSelectedData('celebrity')}
              />
            </div>
            <div className="map-container">
              <svg
                ref={svgRef}
                viewBox="0 0 488 316"
                preserveAspectRatio="xMidYMid meet"
              ></svg>
              <div
                ref={tooltipRef}
                style={{
                  position: 'absolute',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  width: '120px',
                  height: 'auto',
                  padding: '5px',
                  font: '12px sans-serif',
                  background: 'lightsteelblue',
                  border: '0px',
                  borderRadius: '8px',
                  pointerEvents: 'none',
                  opacity: 0
                }}
              ></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="retry-flex">
          <p className="retry-header">Something went wrong...</p>
          <button className="btn btn--primary-outline" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}
    </>
  );
};

export default GeoData;
